import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Copy, RefreshCw, Loader2, Target, CalendarClock, Settings2, Sparkles, TrendingUp, TrendingDown, Info, Home,
  ShieldCheck, AlertTriangle, BookCheck, FlaskConical, Download, Wand2, ChevronLeft, ChevronRight, CheckCircle2,
  History, Save, Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLOR = {
  ALTA: "bg-red-100 text-red-700 border-red-300",
  MEDIA: "bg-amber-100 text-amber-700 border-amber-300",
  BAJA: "bg-slate-100 text-slate-700 border-slate-300",
};

const STATUS_COLOR = {
  red: "bg-red-50 border-red-300 text-red-800",
  yellow: "bg-amber-50 border-amber-300 text-amber-800",
  green: "bg-emerald-50 border-emerald-300 text-emerald-800",
};

const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copiado al portapapeles`);
};

const formatPolicyText = (policy) => {
  const periods = policy.periods
    .filter((p) => p)
    .map(
      (p, idx) =>
        `Período ${idx + 1}: ${p.days} días | desde ${p.available_rooms} disponibles → ${
          p.adjustment_pct >= 0 ? "+" : ""
        }${p.adjustment_pct}%`,
    )
    .join("\n");
  return `${policy.name}\n${periods}`;
};

const PolicyCard = ({ policy, index }) => (
  <Card data-testid={`occ-policy-${index}`} className="border-2 hover:shadow-lg transition-shadow">
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <span className="truncate">{policy.name}</span>
          </CardTitle>
          <CardDescription className="mt-1 text-xs sm:text-sm">{policy.description}</CardDescription>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge className={`${PRIORITY_COLOR[policy.priority] || ""} border text-xs`}>
            {policy.priority}
          </Badge>
          <Button
            data-testid={`copy-policy-${index}`}
            size="sm"
            variant="outline"
            onClick={() => copyToClipboard(formatPolicyText(policy), policy.name)}
          >
            <Copy className="w-4 h-4 mr-1" />
            Copiar
          </Button>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="border-b text-slate-500">
              <th className="text-left py-2 px-2 font-medium">#</th>
              <th className="text-left py-2 px-2 font-medium">Tiempo</th>
              <th className="text-left py-2 px-2 font-medium">Umbral ocupación</th>
              <th className="text-left py-2 px-2 font-medium">Desde X disponibles</th>
              <th className="text-right py-2 px-2 font-medium">Ajuste</th>
            </tr>
          </thead>
          <tbody>
            {policy.periods.map((p, idx) => {
              if (!p) {
                return (
                  <tr key={idx} className="border-b border-slate-100 text-slate-300">
                    <td className="py-2 px-2">{idx + 1}</td>
                    <td colSpan={4} className="py-2 px-2 italic">— vacío —</td>
                  </tr>
                );
              }
              const positive = p.adjustment_pct >= 0;
              const capped = p.capped_by_floor;
              return (
                <tr key={idx} className={`border-b border-slate-100 hover:bg-slate-50 ${capped ? "bg-amber-50" : ""}`}>
                  <td className="py-2 px-2 font-mono text-slate-500">{idx + 1}</td>
                  <td className="py-2 px-2 font-mono">{p.days} días</td>
                  <td className="py-2 px-2">≥ {p.occupancy_threshold_pct}%</td>
                  <td className="py-2 px-2 font-mono font-semibold">{p.available_rooms} hab.</td>
                  <td
                    className={`py-2 px-2 text-right font-mono font-bold ${
                      positive ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {positive ? "+" : ""}
                    {p.adjustment_pct}%
                    {capped && (
                      <div className="text-[10px] font-normal text-amber-700 mt-0.5">
                        🛡️ capado (era {p.original_adjustment_pct}%)
                      </div>
                    )}
                    {!positive && !capped && (
                      <div className="text-[10px] font-normal text-slate-400 mt-0.5">
                        min €{p.min_resulting_price}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900 flex gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>{policy.rationale}</span>
      </div>
    </CardContent>
  </Card>
);

const TimePolicyCard = ({ policy, index }) => {
  const periods = policy.lead_periods || [];
  return (
    <Card data-testid={`time-policy-${index}`} className="border-2 hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <span className="truncate">{policy.name}</span>
            </CardTitle>
            <CardDescription className="mt-1 text-xs sm:text-sm">{policy.description}</CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge className={`${PRIORITY_COLOR[policy.priority] || ""} border text-xs`}>
              {policy.priority}
            </Badge>
            <Button
              data-testid={`copy-time-policy-${index}`}
              size="sm"
              variant="outline"
              onClick={() => {
                const text = `${policy.name}\n${periods
                  .filter((p) => p)
                  .map((p, i) => `Período ${i + 1}: ${p.label}, varía ${p.adjustment_pct >= 0 ? "+" : ""}${p.adjustment_pct}%`)
                  .join("\n")}`;
                copyToClipboard(text, policy.name);
              }}
            >
              <Copy className="w-4 h-4 mr-1" />
              Copiar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b text-slate-500">
                <th className="text-left py-2 px-2 font-medium">#</th>
                <th className="text-left py-2 px-2 font-medium">Periodo (lead time)</th>
                <th className="text-right py-2 px-2 font-medium">Ajuste</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p, idx) => {
                if (!p) {
                  return (
                    <tr key={idx} className="border-b border-slate-100 text-slate-300">
                      <td className="py-2 px-2">{idx + 1}</td>
                      <td colSpan={2} className="py-2 px-2 italic">— vacío —</td>
                    </tr>
                  );
                }
                const positive = p.adjustment_pct >= 0;
                const isZero = p.adjustment_pct === 0;
                return (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 font-mono text-slate-500">{idx + 1}</td>
                    <td className="py-2 px-2">{p.label}</td>
                    <td
                      className={`py-2 px-2 text-right font-mono font-bold ${
                        isZero ? "text-slate-400" : positive ? "text-emerald-600" : "text-rose-600"
                      }`}
                    >
                      {positive ? "+" : ""}
                      {p.adjustment_pct}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 p-3 rounded-md bg-purple-50 border border-purple-200 text-xs text-purple-900 flex gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{policy.rationale}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const CurrentOscarSetup = () => {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  useEffect(() => {
    axios.get(`${API}/api/oscar-rules/current-setup`, { headers })
      .then((r) => setSetup(r.data))
      .catch(() => toast.error("Error cargando setup actual"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const saveSnapshot = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/oscar-rules/snapshot`, {}, { headers });
      toast.success(`Snapshot guardado (${new Date(r.data.created_at).toLocaleString('es-ES')})`);
    } catch {
      toast.error("Error guardando snapshot");
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/api/oscar-rules/snapshots`, { headers });
      setSnapshots(r.data.snapshots || []);
      setShowHistory(true);
    } catch {
      toast.error("Error cargando histórico");
    }
  };

  if (loading) return null;
  if (!setup) return null;

  const dotColor = {
    blue: "bg-blue-500",
    red: "bg-red-500",
    green: "bg-emerald-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
  };

  return (
    <Card className="border-2 border-slate-300 bg-gradient-to-br from-slate-50 to-white" data-testid="current-oscar-setup">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-xl flex items-center gap-2 text-slate-900">
              <Settings2 className="w-5 h-5 text-slate-700" />
              Reglas actuales en Oscar
              <Badge className="bg-slate-200 text-slate-700 border border-slate-300 ml-1">
                {setup.group.total_rooms} hab · {setup.meta.total_periods} periodos
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              Snapshot verificado {setup.meta.last_verified} · {setup.group.name}
              {setup.group.excluded_rooms?.length > 0 && (
                <span className="text-amber-700 ml-2">
                  · ⚠ Excluidas: {setup.group.excluded_rooms.map(r => r.name).join(", ")}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="save-snapshot-btn"
              size="sm"
              variant="outline"
              onClick={saveSnapshot}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar snapshot
            </Button>
            <Button
              data-testid="view-history-btn"
              size="sm"
              variant="outline"
              onClick={loadHistory}
            >
              <History className="w-4 h-4 mr-1" />
              Histórico
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {setup.periods.map((p) => (
            <div
              key={p.index}
              data-testid={`current-period-${p.index}`}
              className="border-2 rounded-lg p-3 bg-white hover:shadow-md transition-shadow"
              style={{ borderColor: `var(--${p.color}-300, #cbd5e1)` }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-3 h-3 rounded-full ${dotColor[p.color] || "bg-slate-400"}`} />
                <span className="text-xs font-mono text-slate-500">P{p.index}</span>
                <span className="text-xs text-slate-400">· {p.days}d</span>
              </div>
              <div className="text-xs font-medium text-slate-700 mb-2 min-h-[32px] leading-tight">
                {p.label}
              </div>
              <div className="space-y-1">
                {p.rules.map((r, i) => {
                  const isPositive = r.adjustment_pct > 0;
                  const isNegative = r.adjustment_pct < 0;
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between text-xs py-1 px-2 rounded bg-slate-50"
                    >
                      <span
                        className={`font-mono font-bold ${
                          isPositive ? "text-emerald-600" : isNegative ? "text-rose-600" : "text-slate-500"
                        }`}
                      >
                        {isPositive ? "+" : ""}{r.adjustment_pct}%
                      </span>
                      <span className="text-slate-500">
                        disp ≤ {r.when_availability}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Histórico de snapshots</DialogTitle>
              <DialogDescription>
                Instantáneas guardadas de las reglas de Oscar a lo largo del tiempo.
              </DialogDescription>
            </DialogHeader>
            {snapshots.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                No hay snapshots guardados aún. Pulsa "Guardar snapshot" para crear el primero.
              </p>
            ) : (
              <div className="space-y-2">
                {snapshots.map((s) => (
                  <div key={s.id} className="border rounded p-3 flex items-center gap-3">
                    <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-sm">
                      <div className="font-medium">
                        {new Date(s.created_at).toLocaleString('es-ES')}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {s.created_by} · {s.setup?.meta?.total_periods || 0} periodos · {s.setup?.group?.total_rooms || 0} hab
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const GroupSelector = ({ groups, selected, onSelect }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    {groups.map((g) => {
      const active = g.key === selected;
      return (
        <button
          key={g.key}
          data-testid={`group-tab-${g.key}`}
          onClick={() => onSelect(g.key)}
          className={`text-left p-4 rounded-xl border-2 transition-all ${
            active
              ? "border-blue-600 bg-blue-50 shadow-md"
              : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">{g.icon}</span>
            <span className={`font-bold text-sm ${active ? "text-blue-700" : "text-slate-700"}`}>
              {g.label}
            </span>
          </div>
          <div className="text-xs text-slate-500 line-clamp-2">{g.description}</div>
          <div className="mt-2 text-xs font-mono font-semibold text-slate-600">
            {g.total_qty} habitaciones
          </div>
        </button>
      );
    })}
  </div>
);

import OscarShortcuts from "../components/OscarShortcuts";

const buildWizardSteps = (data) => {
  if (!data) return [];
  const steps = [
    {
      title: "Bienvenida 👋",
      subtitle: `Vamos a crear ${data.occupancy_policies.length} políticas de ocupación + ${data.time_policies.length} de lead time en Oscar. Tardarás ~15 min.`,
      where: "Aún no entres en Oscar. Solo lee esto.",
      action: (
        <div className="space-y-3 text-sm">
          <p>Antes de empezar, comprueba que tienes:</p>
          <ul className="list-disc list-inside text-slate-700 space-y-1">
            <li>✅ El contrato RMA <strong>activado</strong> en Oscar (€0.99/día)</li>
            <li>✅ El <strong>precio mínimo €{data.min_price_floor}</strong> configurado en Tarifas</li>
            <li>✅ Esta ventana abierta y otra ventana con Oscar</li>
            <li>✅ ~15 minutos sin interrupciones</li>
          </ul>
          <p className="text-amber-700 font-semibold">
            💡 Tip: descarga primero el PDF (botón de arriba) para tenerlo al lado.
          </p>
        </div>
      ),
    },
    {
      title: "Abre Oscar en otra pestaña",
      subtitle: "Ve a oscar.redforts.com y haz login.",
      where: "Navegador → Oscar PMS",
      action: (
        <div className="text-sm space-y-3">
          <p>1. Abre <code className="bg-slate-100 px-1.5 py-0.5 rounded">oscar.redforts.com</code> en otra pestaña</p>
          <p>2. Haz login con tus credenciales</p>
          <p>3. Vuelve aquí y dale a "Siguiente"</p>
        </div>
      ),
    },
  ];

  // Steps per occupancy policy
  data.occupancy_policies.forEach((policy, idx) => {
    const periods = (policy.periods || []).filter(Boolean);
    steps.push({
      title: `Política ${idx + 1}/${data.occupancy_policies.length}: ${policy.name}`,
      subtitle: policy.description,
      where: "Oscar → Ajustes → Revenue management → Políticas basadas en ocupación → + Añadir",
      action: (
        <div className="space-y-3 text-sm">
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="font-semibold mb-1">📝 Nombre de la política:</p>
            <code className="block bg-white border rounded px-2 py-1.5 text-sm font-mono">{policy.name}</code>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <p className="font-semibold mb-2">🏠 Alojamientos a marcar ({data.group_total_qty} en total):</p>
            <ul className="text-xs space-y-0.5">
              {(data.group_rooms || []).map((r, i) => (
                <li key={i} className="flex justify-between">
                  <span>☑ {r.name}</span>
                  <span className="font-mono text-slate-600">{r.qty} hab.</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <p className="font-semibold mb-2">⚙️ Reglas de revenue ({periods.length} períodos):</p>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-amber-300">
                  <th className="text-left py-1">#</th>
                  <th className="text-left py-1">Llegadas hoy y los siguientes</th>
                  <th className="text-left py-1">% ajuste cuando disp. sea</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p, i) => (
                  <tr key={i} className="border-b border-amber-200">
                    <td className="py-1.5 font-mono">{i + 1}</td>
                    <td className="py-1.5 font-mono font-bold">{p.days} días</td>
                    <td className="py-1.5 font-mono">
                      <span className={p.adjustment_pct >= 0 ? "text-emerald-700" : "text-rose-700"}>
                        {p.adjustment_pct >= 0 ? "+" : ""}{p.adjustment_pct}%
                      </span>
                      {" cuando ≤ "}
                      <strong>{p.available_rooms}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-600">
            <strong>Pasos:</strong> 1) Pulsa "+ Añadir" en Oscar → 2) Pega Nombre → 3) Marca los alojamientos →
            4) Añade los {periods.length} períodos uno por uno → 5) Comprobar política → 6) Guardar
          </p>
        </div>
      ),
    });
  });

  // Steps per time policy
  data.time_policies.forEach((policy, idx) => {
    const periods = (policy.lead_periods || []).filter(Boolean);
    steps.push({
      title: `Lead time ${idx + 1}/${data.time_policies.length}: ${policy.name}`,
      subtitle: policy.description,
      where: "Oscar → Ajustes → Revenue management → Políticas basadas en tiempo → + Añadir",
      action: (
        <div className="space-y-3 text-sm">
          <div className="bg-purple-50 border border-purple-200 rounded p-3">
            <p className="font-semibold mb-1">📝 Nombre de la política:</p>
            <code className="block bg-white border rounded px-2 py-1.5 text-sm font-mono">{policy.name}</code>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <p className="font-semibold mb-2">⏱️ Períodos ({periods.length}):</p>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-amber-300">
                  <th className="text-left py-1">#</th>
                  <th className="text-left py-1">Texto exacto a poner</th>
                  <th className="text-right py-1">%</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p, i) => (
                  <tr key={i} className="border-b border-amber-200">
                    <td className="py-1.5 font-mono">{i + 1}</td>
                    <td className="py-1.5">{p.label}</td>
                    <td className="py-1.5 text-right font-mono font-bold">
                      <span className={p.adjustment_pct >= 0 ? "text-emerald-700" : "text-rose-700"}>
                        {p.adjustment_pct >= 0 ? "+" : ""}{p.adjustment_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-600">
            <strong>Pasos:</strong> 1) "+ Añadir" en Políticas basadas en tiempo → 2) Pega el Nombre →
            3) Añade los períodos uno por uno (días + %) → 4) Mira el gráfico de variación → 5) Guardar
          </p>
        </div>
      ),
    });
  });

  // Final step
  steps.push({
    title: "¡Terminado! 🎉",
    subtitle: "Has configurado todas las reglas de Revenue Management en Oscar.",
    where: "Oscar → Comprobar configuración",
    action: (
      <div className="space-y-3 text-sm">
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
          <p className="font-bold text-lg text-emerald-900">¡Todo configurado!</p>
          <p className="text-emerald-700 mt-1">
            {data.occupancy_policies.length} políticas de ocupación + {data.time_policies.length} de lead time activas.
          </p>
        </div>
        <p className="font-semibold">Próximos pasos:</p>
        <ol className="list-decimal list-inside text-slate-700 space-y-1">
          <li>Ve a Oscar → <strong>Revenue management → Comprobar configuración</strong></li>
          <li>Verifica que todas las políticas aparecen y se cruzan con las tarifas</li>
          <li>Monitoriza durante <strong>14 días</strong> tu pickup rate y ADR</li>
          <li>Si pickup cae &gt;15% → desactiva alguna política o suaviza %</li>
          <li>Repega las reglas <strong>1 vez al mes</strong> (los % se recalculan según tu gap)</li>
        </ol>
      </div>
    ),
  });

  return steps;
};


const OscarRules = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState("global");
  const [upliftsOnly, setUpliftsOnly] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [backtest, setBacktest] = useState(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchRules = useCallback(async (g, uplifts) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/oscar-rules/generate?group=${g}&uplifts_only=${uplifts}`,
        { headers },
      );
      setData(res.data);
    } catch (e) {
      toast.error("Error generando reglas Oscar");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchRules(group, upliftsOnly);
  }, [fetchRules, group, upliftsOnly]);

  const runPriceAudit = async () => {
    setAuditLoading(true);
    setAudit(null);
    try {
      toast.info("Auditando precios actuales en Oscar (puede tardar 60s)...");
      const res = await axios.get(`${API}/api/oscar-rules/price-audit?days_ahead=60`, {
        headers,
        timeout: 120000,
      });
      setAudit(res.data);
      if (res.data.at_risk_count === 0) {
        toast.success("✅ Auditoría OK: ningún día bajo €80");
      } else {
        toast.warning(`⚠️ ${res.data.at_risk_count} día(s) en riesgo detectados`);
      }
    } catch (e) {
      toast.error("Error auditando precios");
    } finally {
      setAuditLoading(false);
    }
  };

  const runBacktest = async () => {
    setBacktestLoading(true);
    setBacktest(null);
    try {
      const res = await axios.get(
        `${API}/api/oscar-rules/backtest?year=2025&group=${group}&uplifts_only=${upliftsOnly}`,
        { headers },
      );
      setBacktest(res.data);
      const sign = res.data.total_uplift >= 0 ? "+" : "";
      toast.success(`Backtest: ${sign}€${Math.round(res.data.total_uplift).toLocaleString()} (${sign}${res.data.uplift_pct}%)`);
    } catch (e) {
      toast.error("Error ejecutando backtest");
    } finally {
      setBacktestLoading(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/oscar-rules/pdf?group=${group}&uplifts_only=${upliftsOnly}`,
        { headers, responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const filename = `oscar_rules_${group}_${today}.pdf`;

      // Try standard download first
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Safari/iOS fallback: open in new tab after a short delay
      // (if download didn't trigger, user can save manually from preview)
      setTimeout(() => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
          window.open(url, "_blank");
        }
        window.URL.revokeObjectURL(url);
      }, 1500);

      toast.success("PDF descargado");
    } catch (e) {
      console.error("PDF download error:", e);
      toast.error("Error descargando PDF. Revisa la consola.");
    } finally {
      setPdfLoading(false);
    }
  };

  const download2027Tariffs = async () => {
    try {
      const res = await axios.get(`${API}/api/oscar-rules/tariffs-2027`, {
        headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Tarifas_2027_Port_Sitges.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel Tarifas 2027 descargado");
    } catch (e) {
      toast.error("Error descargando Excel 2027");
    }
  };

  const downloadPlanPdf = async () => {
    try {
      const res = await axios.get(`${API}/api/reports/plan-1900k/pdf`, {
        headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Plan_1.9M_PortSitges_${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plan 1.9M PDF descargado");
    } catch (e) {
      toast.error("Error descargando Plan PDF");
    }
  };

  const downloadPlanXlsx = async () => {
    try {
      const res = await axios.get(`${API}/api/reports/plan-1900k/xlsx`, {
        headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Plan_1.9M_PortSitges_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plan 1.9M Excel descargado");
    } catch (e) {
      toast.error("Error descargando Plan Excel");
    }
  };

  const downloadPrices7pct = async () => {
    try {
      const res = await axios.get(`${API}/api/reports/prices-7pct/pdf`, {
        headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Tarifas_+7pct_PortSitges_${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Tarifas +7% descargadas");
    } catch (e) {
      toast.error("Error descargando tarifas +7%");
    }
  };

  const openWizard = () => {
    setWizardStep(0);
    setShowWizard(true);
  };

  const copyAllOccupancy = () => {
    if (!data) return;
    const text = data.occupancy_policies.map(formatPolicyText).join("\n\n");
    navigator.clipboard.writeText(text);
    toast.success(`${data.occupancy_policies.length} políticas copiadas`);
  };

  const copyAllTime = () => {
    if (!data) return;
    const text = data.time_policies
      .map((policy) => {
        const periods = (policy.lead_periods || [])
          .filter((p) => p)
          .map(
            (p, i) =>
              `Período ${i + 1}: ${p.label}, varía ${p.adjustment_pct >= 0 ? "+" : ""}${p.adjustment_pct}%`,
          )
          .join("\n");
        return `${policy.name}\n${periods}`;
      })
      .join("\n\n");
    navigator.clipboard.writeText(text);
    toast.success(`${data.time_policies.length} políticas de tiempo copiadas`);
  };

  const copyRoomList = () => {
    if (!data?.group_rooms) return;
    const text = data.group_rooms.map((r) => `- ${r.name} (${r.qty} hab.)`).join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Lista de habitaciones copiada");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-slate-500">
        No se pudieron generar las reglas. Reintenta.
      </div>
    );
  }

  const gapBadge = data.gap_pct < 0 ? (
    <span className="inline-flex items-center gap-1 text-rose-600 font-semibold">
      <TrendingDown className="w-4 h-4" />
      {data.gap_pct}%
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
      <TrendingUp className="w-4 h-4" />+{data.gap_pct}%
    </span>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto" data-testid="oscar-rules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">
            Reglas para Oscar
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Políticas de Revenue Management listas para pegar en Oscar PMS.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="open-wizard-btn"
            onClick={openWizard}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Asistente paso a paso
          </Button>
          <Button
            data-testid="download-pdf-btn"
            variant="outline"
            onClick={downloadPdf}
            disabled={pdfLoading}
          >
            {pdfLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Descargar PDF
          </Button>
          <Button
            data-testid="download-2027-btn"
            onClick={download2027Tariffs}
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
          >
            <Download className="w-4 h-4 mr-2" />
            Tarifas 2027 📅
          </Button>
          <Button
            data-testid="download-plan-pdf-btn"
            onClick={downloadPlanPdf}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Plan 1.9M PDF
          </Button>
          <Button
            data-testid="download-plan-xlsx-btn"
            onClick={downloadPlanXlsx}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Plan 1.9M Excel
          </Button>
          <Button
            data-testid="download-prices-7pct-btn"
            onClick={downloadPrices7pct}
            className="bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-700 hover:to-pink-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Tarifas +7% 💰
          </Button>
          <Button
            data-testid="run-audit-btn"
            variant="outline"
            onClick={runPriceAudit}
            disabled={auditLoading}
          >
            {auditLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ShieldCheck className="w-4 h-4 mr-2" />
            )}
            Auditar precios
          </Button>
          <Button
            data-testid="run-backtest-btn"
            variant="outline"
            onClick={runBacktest}
            disabled={backtestLoading}
            className="bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 border-purple-200"
          >
            {backtestLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FlaskConical className="w-4 h-4 mr-2 text-purple-600" />
            )}
            Backtest 2025
          </Button>
          <Dialog open={showChecklist} onOpenChange={setShowChecklist}>
            <DialogTrigger asChild>
              <Button data-testid="show-checklist-btn" variant="outline">
                <BookCheck className="w-4 h-4 mr-2" />
                Checklist Oscar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl">Checklist antes de activar Oscar RMA</DialogTitle>
                <DialogDescription>
                  Sigue estos pasos en orden para proteger tu margen al 100%.
                </DialogDescription>
              </DialogHeader>
              <ol className="space-y-4 text-sm mt-4">
                <li className="flex gap-3 p-3 rounded-lg bg-slate-50 border">
                  <span className="font-mono font-bold text-blue-600 text-lg flex-shrink-0">1.</span>
                  <div>
                    <p className="font-semibold mb-1">Auditar precios actuales</p>
                    <p className="text-slate-600">
                      Pulsa <strong>"Auditar precios"</strong> arriba para ver si tienes días con tarifa
                      bajo €80 en los próximos 60 días. Revisa y corrige los outliers en Oscar antes de
                      activar reglas.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3 p-3 rounded-lg bg-slate-50 border">
                  <span className="font-mono font-bold text-blue-600 text-lg flex-shrink-0">2.</span>
                  <div>
                    <p className="font-semibold mb-1">Configurar precio mínimo absoluto en Oscar</p>
                    <p className="text-slate-600 mb-2">
                      Esto es <strong>la red de seguridad real</strong>: Oscar nunca venderá por debajo de este
                      precio, da igual qué regla aplique.
                    </p>
                    <div className="bg-white border rounded p-3 font-mono text-xs space-y-1">
                      <div>• Oscar → <strong>Sistema de gestión → Tarifas</strong></div>
                      <div>• Selecciona la tarifa <strong>"Tarifas de precios manuales"</strong></div>
                      <div>• En cada tipo de habitación → <strong>"Precio mínimo"</strong> = <span className="bg-amber-100 px-1">€59</span></div>
                      <div>• <strong>Guardar</strong></div>
                    </div>
                  </div>
                </li>
                <li className="flex gap-3 p-3 rounded-lg bg-slate-50 border">
                  <span className="font-mono font-bold text-blue-600 text-lg flex-shrink-0">3.</span>
                  <div>
                    <p className="font-semibold mb-1">Empezar en modo "Solo Uplifts"</p>
                    <p className="text-slate-600">
                      Activa el switch <strong>"Solo Uplifts"</strong> arriba: genera reglas que solo
                      <strong> suben</strong> precio nunca bajan. Cero riesgo de margen durante las primeras 2 semanas.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3 p-3 rounded-lg bg-slate-50 border">
                  <span className="font-mono font-bold text-blue-600 text-lg flex-shrink-0">4.</span>
                  <div>
                    <p className="font-semibold mb-1">Activar el contrato RMA en Oscar (€0.99/día)</p>
                    <p className="text-slate-600">
                      Oscar → <strong>Revenue management → Comprobar configuración</strong> → "Cambiar contrato"
                      → marca <strong>"Activado"</strong>.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3 p-3 rounded-lg bg-slate-50 border">
                  <span className="font-mono font-bold text-blue-600 text-lg flex-shrink-0">5.</span>
                  <div>
                    <p className="font-semibold mb-1">Crear la política Global con las 4 reglas</p>
                    <p className="text-slate-600">
                      Empieza con <strong>Política Global</strong> (todos los tipos seleccionados). Después de 14 días,
                      si todo va bien, expandes a las 3 políticas por grupo.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3 p-3 rounded-lg bg-slate-50 border">
                  <span className="font-mono font-bold text-blue-600 text-lg flex-shrink-0">6.</span>
                  <div>
                    <p className="font-semibold mb-1">Usar "Comprobar su política" antes de guardar</p>
                    <p className="text-slate-600">
                      Oscar tiene un simulador integrado: <strong>"Si para el [X] la disponibilidad es [Y], el precio será [Z]"</strong>.
                      Prueba con 3-4 fechas críticas antes de Guardar.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-300">
                  <span className="font-mono font-bold text-emerald-600 text-lg flex-shrink-0">✓</span>
                  <div>
                    <p className="font-semibold mb-1 text-emerald-900">Monitorizar 14 días</p>
                    <p className="text-emerald-800">
                      Revisa diariamente: ADR, pickup rate y cancelaciones. Si pickup cae &gt;15% →
                      desactiva y suaviza %.
                    </p>
                  </div>
                </li>
              </ol>
            </DialogContent>
          </Dialog>
          <Button data-testid="refresh-rules-btn" onClick={() => fetchRules(group, upliftsOnly)} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerar
          </Button>
        </div>
      </div>

      {/* Reglas actuales en Oscar (snapshot real de 67 hab) */}
      <CurrentOscarSetup />

      {/* Quick shortcuts to Oscar */}
      <OscarShortcuts />

      {/* Mode toggle: Uplifts only */}
      <Card className={`border-2 ${upliftsOnly ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <ShieldCheck className={`w-6 h-6 flex-shrink-0 ${upliftsOnly ? "text-emerald-600" : "text-slate-400"}`} />
            <div className="flex-1 min-w-0">
              <Label htmlFor="uplifts-toggle" className="text-base font-bold cursor-pointer">
                Modo "Solo Uplifts" {upliftsOnly && <Badge className="bg-emerald-100 text-emerald-700 ml-2">ACTIVO</Badge>}
              </Label>
              <p className="text-xs text-slate-600 mt-1">
                Elimina TODOS los descuentos. Solo se aplican subidas de precio cuando hay alta ocupación.
                Recomendado para las primeras 2 semanas de activación. <strong>Cero riesgo de margen.</strong>
              </p>
            </div>
          </div>
          <Switch
            id="uplifts-toggle"
            data-testid="uplifts-only-toggle"
            checked={upliftsOnly}
            onCheckedChange={setUpliftsOnly}
          />
        </CardContent>
      </Card>

      {/* Backtest results panel */}
      {backtest && (
        <Card data-testid="backtest-panel" className={`border-2 ${backtest.total_uplift >= 0 ? "border-emerald-400 bg-gradient-to-br from-emerald-50 to-white" : "border-rose-400 bg-rose-50"}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-xl flex items-center gap-2">
              <FlaskConical className={`w-6 h-6 ${backtest.total_uplift >= 0 ? "text-emerald-600" : "text-rose-600"}`} />
              Backtest {backtest.year} — {backtest.group_label}
              {backtest.uplifts_only_mode && (
                <Badge className="bg-emerald-100 text-emerald-700">Solo Uplifts</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Si estas reglas hubieran estado activas en {backtest.year}, así habría sido el resultado.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-white border">
                <div className="text-xs text-slate-500 mb-1">Real {backtest.year}</div>
                <div className="text-lg sm:text-xl font-bold font-mono">
                  €{(backtest.total_real / 1000).toFixed(0)}K
                </div>
              </div>
              <div className="p-3 rounded-lg bg-white border">
                <div className="text-xs text-slate-500 mb-1">Simulado con reglas</div>
                <div className="text-lg sm:text-xl font-bold font-mono text-blue-600">
                  €{(backtest.total_simulated / 1000).toFixed(0)}K
                </div>
              </div>
              <div className={`p-3 rounded-lg border-2 ${backtest.total_uplift >= 0 ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}>
                <div className="text-xs text-slate-600 mb-1 font-semibold">Uplift</div>
                <div className={`text-lg sm:text-xl font-bold font-mono ${backtest.total_uplift >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {backtest.total_uplift >= 0 ? "+" : ""}€{Math.round(backtest.total_uplift).toLocaleString()}
                </div>
                <div className={`text-xs ${backtest.total_uplift >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {backtest.uplift_pct >= 0 ? "+" : ""}{backtest.uplift_pct}%
                </div>
              </div>
              <div className={`p-3 rounded-lg border-2 ${backtest.would_hit_goal ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}>
                <div className="text-xs text-slate-600 mb-1 font-semibold">Proyección 2026</div>
                <div className="text-lg sm:text-xl font-bold font-mono">
                  €{(backtest.projected_2026_with_rules / 1000).toFixed(0)}K
                </div>
                <div className={`text-xs font-semibold ${backtest.would_hit_goal ? "text-emerald-600" : "text-amber-600"}`}>
                  {backtest.would_hit_goal ? "✅ Supera €1.9M" : `❌ Falta €${Math.round(backtest.distance_to_goal_after_rules / 1000)}K`}
                </div>
              </div>
            </div>

            {/* Monthly table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left py-2 px-2 font-semibold">Mes</th>
                    <th className="text-right py-2 px-2 font-semibold">Ocupación</th>
                    <th className="text-right py-2 px-2 font-semibold">ADR real</th>
                    <th className="text-right py-2 px-2 font-semibold">ADR sim.</th>
                    <th className="text-right py-2 px-2 font-semibold">Ajuste</th>
                    <th className="text-right py-2 px-2 font-semibold">Real €</th>
                    <th className="text-right py-2 px-2 font-semibold">Sim. €</th>
                    <th className="text-right py-2 px-2 font-semibold">Uplift €</th>
                  </tr>
                </thead>
                <tbody>
                  {backtest.monthly_simulations.map((m) => {
                    const positive = m.uplift_eur >= 0;
                    return (
                      <tr key={m.month} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-2 font-semibold">{m.month_name}</td>
                        <td className="py-2 px-2 text-right font-mono">{m.occupancy_pct}%</td>
                        <td className="py-2 px-2 text-right font-mono">€{m.adr_real.toFixed(0)}</td>
                        <td className="py-2 px-2 text-right font-mono text-blue-600">€{m.adr_simulated.toFixed(0)}</td>
                        <td className={`py-2 px-2 text-right font-mono font-bold ${m.adjustment_pct > 0 ? "text-emerald-600" : m.adjustment_pct < 0 ? "text-rose-600" : "text-slate-400"}`}>
                          {m.adjustment_pct > 0 ? "+" : ""}{m.adjustment_pct}%
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-500">€{Math.round(m.revenue_real).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right font-mono">€{Math.round(m.revenue_simulated).toLocaleString()}</td>
                        <td className={`py-2 px-2 text-right font-mono font-bold ${positive ? "text-emerald-600" : "text-rose-600"}`}>
                          {positive ? "+" : ""}€{Math.round(m.uplift_eur).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-100 font-bold">
                  <tr>
                    <td className="py-2 px-2" colSpan={5}>TOTAL</td>
                    <td className="py-2 px-2 text-right font-mono">€{Math.round(backtest.total_real).toLocaleString()}</td>
                    <td className="py-2 px-2 text-right font-mono">€{Math.round(backtest.total_simulated).toLocaleString()}</td>
                    <td className={`py-2 px-2 text-right font-mono ${backtest.total_uplift >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {backtest.total_uplift >= 0 ? "+" : ""}€{Math.round(backtest.total_uplift).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Assumptions */}
            <details className="text-xs text-slate-600 bg-slate-50 rounded p-3">
              <summary className="cursor-pointer font-semibold">Supuestos del modelo</summary>
              <ul className="list-disc list-inside mt-2 space-y-1">
                {backtest.assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </details>
          </CardContent>
        </Card>
      )}

      {/* Audit results panel */}
      {audit && (
        <Card data-testid="audit-panel" className={`border-2 ${audit.at_risk_count > 0 ? "border-amber-400 bg-amber-50" : "border-emerald-400 bg-emerald-50"}`}>
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              {audit.at_risk_count > 0 ? (
                <AlertTriangle className="w-7 h-7 text-amber-600 flex-shrink-0" />
              ) : (
                <ShieldCheck className="w-7 h-7 text-emerald-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-2">
                  Auditoría de precios: {audit.scanned_days} días escaneados
                </h3>
                {audit.at_risk_count === 0 ? (
                  <p className="text-sm text-emerald-800">
                    ✅ Ningún día con precio bajo €{audit.min_safe_price}. Puedes activar las reglas con tranquilidad.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-amber-900 mb-3">
                      ⚠️ <strong>{audit.at_risk_count}</strong> día(s) con precio bajo €{audit.min_safe_price}
                      {audit.below_floor_count > 0 && (
                        <span className="text-rose-700"> ({audit.below_floor_count} bajo €{audit.floor} 🚨)</span>
                      )}.
                      Revisa estos antes de activar reglas con descuento.
                    </p>
                    <div className="bg-white rounded border max-h-60 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left py-2 px-3">Fecha</th>
                            <th className="text-left py-2 px-3">Tipo</th>
                            <th className="text-right py-2 px-3">Precio actual</th>
                            <th className="text-right py-2 px-3">Max descuento seguro</th>
                          </tr>
                        </thead>
                        <tbody>
                          {audit.at_risk_days.map((d, i) => (
                            <tr key={i} className={`border-b ${d.below_floor ? "bg-rose-50" : ""}`}>
                              <td className="py-2 px-3 font-mono">{d.date}</td>
                              <td className="py-2 px-3 truncate">{d.room_name}</td>
                              <td className="py-2 px-3 text-right font-mono font-bold">
                                €{d.current_price}
                                {d.below_floor && <span className="ml-1 text-rose-600">🚨</span>}
                              </td>
                              <td className="py-2 px-3 text-right font-mono text-rose-600">
                                -{d.max_safe_discount_pct}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Group selector */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Home className="w-5 h-5 text-blue-600" />
            Elige qué grupo de habitaciones configurar
          </CardTitle>
          <CardDescription>
            Cada grupo tiene reglas distintas según su elasticidad al precio. Empieza por "Global" para validar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <GroupSelector
            groups={data.available_groups || []}
            selected={data.selected_group}
            onSelect={setGroup}
          />
        </CardContent>
      </Card>

      {/* Status banner */}
      <Card className={`border-2 ${STATUS_COLOR[data.status_color] || STATUS_COLOR.yellow}`}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
            <Target className="w-8 h-8 flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-bold text-lg">{data.status_label}</span>
                <span className="text-sm">| Gap vs ritmo €1.9M: {gapBadge}</span>
              </div>
              <p className="text-sm mt-2 opacity-90">
                YTD Estancias: <strong>€{(data.current_ytd / 1000).toFixed(1)}K</strong> de{" "}
                <strong>€{(data.goal_amount / 1000).toFixed(0)}K</strong> | Mult. agresividad:{" "}
                <strong>{data.aggressiveness_multiplier}x</strong> | Grupo:{" "}
                <strong>{data.group_icon} {data.group_label} ({data.group_total_qty} hab.)</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Group info: rooms + floor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Rooms in this group */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Home className="w-4 h-4 text-blue-600" />
                Habitaciones de este grupo ({data.group_total_qty})
              </CardTitle>
              <Button data-testid="copy-room-list-btn" size="sm" variant="outline" onClick={copyRoomList}>
                <Copy className="w-3.5 h-3.5 mr-1" />
                Copiar
              </Button>
            </div>
            <CardDescription className="text-xs">
              Selecciona estos en "Alojamientos" al crear la política en Oscar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1">
              {(data.group_rooms || []).map((r, i) => (
                <li key={i} className="flex justify-between border-b border-slate-100 py-1.5">
                  <span className="truncate pr-2">{r.name}</span>
                  <span className="font-mono font-semibold text-slate-600 flex-shrink-0">
                    {r.qty} hab. · €{r.base_price}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Price floor banner */}
        <Card className="bg-slate-900 text-white border-slate-900">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="text-3xl">🛡️</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-base sm:text-lg">
                  Protección de margen: precio mínimo €{data.min_price_floor}
                </div>
                <div className="text-xs sm:text-sm text-slate-300 mt-2 space-y-1">
                  <div>
                    Habitación más barata del grupo: <strong>€{data.group_min_base_price}</strong>
                  </div>
                  <div>
                    Habitación más cara del grupo: <strong>€{data.group_max_base_price}</strong>
                  </div>
                  <div className="pt-1 border-t border-slate-700">
                    Descuento máximo permitido:{" "}
                    <strong className="text-amber-300">-{data.max_discount_pct}%</strong>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="bg-slate-50 border-dashed">
        <CardContent className="p-4 text-sm space-y-2">
          <p className="font-semibold text-slate-700">Cómo pegar en Oscar:</p>
          <ol className="list-decimal list-inside space-y-1 text-slate-600">
            <li>
              En Oscar: <strong>Revenue management → Políticas basadas en ocupación → + Añadir</strong>.
            </li>
            <li>
              <strong>Nombre</strong>: copia exacto el de la tarjeta.
            </li>
            <li>
              <strong>Alojamientos</strong>: marca los {data.group_total_qty} de la lista de arriba.
            </li>
            <li>
              <strong>Reglas de revenue</strong>: añade {data.occupancy_policies[0]?.periods?.filter(Boolean).length || 5} periodos
              con sus reglas (días + % + disponibilidad).
            </li>
            <li>
              <strong>Comprobar política</strong>: usa el simulador integrado antes de Guardar.
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Occupancy Policies */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings2 className="w-6 h-6 text-blue-600" />
            Políticas basadas en ocupación ({data.occupancy_policies.length})
          </h2>
          <Button data-testid="copy-all-occupancy-btn" variant="outline" onClick={copyAllOccupancy}>
            <Copy className="w-4 h-4 mr-2" />
            Copiar todas
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.occupancy_policies.map((policy, idx) => (
            <PolicyCard key={idx} policy={policy} index={idx} />
          ))}
        </div>
      </div>

      {/* Time Policies */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-purple-600" />
            Políticas basadas en tiempo / Lead time ({data.time_policies.length})
          </h2>
          <Button data-testid="copy-all-time-btn" variant="outline" onClick={copyAllTime}>
            <Copy className="w-4 h-4 mr-2" />
            Copiar todas
          </Button>
        </div>
        <Card className="bg-purple-50 border-purple-200 mb-4">
          <CardContent className="p-3 text-xs sm:text-sm text-purple-900 flex gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Formato Oscar:</strong> estas políticas ajustan el precio según
              <strong> cuántos días faltan hasta la llegada</strong> del huésped (lead time), no fechas concretas.
              Crea en Oscar → <strong>Revenue management → Políticas basadas en tiempo → + Añadir</strong>.
            </div>
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.time_policies.length === 0 ? (
            <div className="col-span-2 py-6 text-center text-slate-400 italic">
              No hay políticas generadas.
            </div>
          ) : (
            data.time_policies.map((policy, idx) => (
              <TimePolicyCard key={idx} policy={policy} index={idx} />
            ))
          )}
        </div>
      </div>

      {/* Footer note */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 text-sm text-amber-900 flex gap-2">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Reglas adaptativas por grupo:</strong> los porcentajes se recalculan en función de
            la elasticidad de cada grupo y tu gap al €1.9M. Cambia entre grupos arriba para ver las
            reglas específicas. Repega en Oscar al menos 1 vez al mes para mantenerlas actualizadas.
          </div>
        </CardContent>
      </Card>

      {/* Wizard Dialog */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wizard-dialog">
          {(() => {
            const steps = buildWizardSteps(data);
            const current = steps[wizardStep] || {};
            const total = steps.length;
            const progress = ((wizardStep + 1) / total) * 100;
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-blue-100 text-blue-700">
                      Paso {wizardStep + 1} de {total}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {Math.round(progress)}% completado
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <DialogTitle className="text-2xl mt-4">{current.title}</DialogTitle>
                  <DialogDescription>{current.subtitle}</DialogDescription>
                </DialogHeader>

                {current.where && (
                  <div className="bg-slate-100 border-l-4 border-blue-500 p-3 text-xs sm:text-sm">
                    <span className="font-semibold text-slate-600">📍 Dónde: </span>
                    <span className="text-slate-800">{current.where}</span>
                  </div>
                )}

                <div className="mt-2">{current.action}</div>

                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <Button
                    data-testid="wizard-prev-btn"
                    variant="outline"
                    onClick={() => setWizardStep(Math.max(0, wizardStep - 1))}
                    disabled={wizardStep === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Atrás
                  </Button>
                  <span className="text-sm text-slate-500">
                    {wizardStep + 1} / {total}
                  </span>
                  {wizardStep < total - 1 ? (
                    <Button
                      data-testid="wizard-next-btn"
                      onClick={() => setWizardStep(wizardStep + 1)}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                    >
                      Siguiente
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      data-testid="wizard-close-btn"
                      onClick={() => setShowWizard(false)}
                      className="bg-emerald-600 text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Terminado
                    </Button>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OscarRules;
